import type { ReactNode } from 'react';

import { Dropdown } from '#/components/Dropdown/Dropdown';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from '#/routing/route-constants';
import { postAListingRoute } from '#/routing/routes';

import {
  PostOnIdealistDropdownDesktopText,
  PostOnIdealistDropdownMobileText,
} from './PostOnIdealistDropdown.styled';
import { PostOnIdealistDropdownMenu } from './PostOnIdealistDropdownMenu';

type Props = {
  renderTrigger: (data: {
    triggerText: ReactNode;
    triggerHref: string;
  }) => ReactNode;
  trackClickedContext: string;
};

const triggerTextByLocale: Record<
  I18nLocale,
  { text: string; href: string; mobileText?: string }
> = {
  en: {
    mobileText: 'Post',
    text: 'Post on Idealist',
    href: postAListingRoute.with({}),
  },
  es: {
    text: 'Publica',
    href: postAListingRoute.with({}),
  },
  pt: {
    text: 'Anunciar',
    href: postAListingRoute.with({}),
  },
};

const triggerTextByLocaleUnauth: Record<
  I18nLocale,
  { text: string; href: string; mobileText?: string }
> = {
  en: {
    mobileText: 'Post',
    text: 'Post on Idealist',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.en,
  },
  es: {
    text: 'Publica',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.es,
  },
  pt: {
    text: 'Anunciar',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.pt,
  },
};

export function PostOnIdealistDropdown({
  renderTrigger,
  trackClickedContext,
}: Props) {
  const { locale } = useI18n();
  const { user } = useUser();

  const { mobileText, text, href } = user
    ? triggerTextByLocale[locale]
    : triggerTextByLocaleUnauth[locale];

  return (
    <Dropdown
      trigger={renderTrigger({
        triggerText: mobileText ? (
          <>
            <PostOnIdealistDropdownDesktopText>
              {text}
            </PostOnIdealistDropdownDesktopText>

            <PostOnIdealistDropdownMobileText>
              {mobileText}
            </PostOnIdealistDropdownMobileText>
          </>
        ) : (
          text
        ),
        triggerHref: href,
      })}
      desktopTriggersOnHover
    >
      <PostOnIdealistDropdownMenu trackClickedContext={trackClickedContext} />
    </Dropdown>
  );
}
